<template>
    <div
        id="seekBar"
        ref="seekBar"
        class="seek_bar"
        @click="eventLine($event, Dragger.Click)"
    >
        <div ref="slider_line" class="slider_line" data-cy="slider-line">
            <div ref="nodeSlider" class="slider_line_node" @mousedown="dragElement()">
                <span v-if="showPercentage" ref="percentage" class="percentage">{{ selected }}%</span>
            </div>
        </div>
        <div class="line"></div>
        <SeekNodes
            v-if="showNodes"
            :bg="nodeColor"
            :count="nodeCount"
            :line-color="lineColor"
            :select-node="selected"
            :size="nodeSize"
            :text-color="colorTextPercent"
            :transition="transition"
        />
    </div>
</template>

<script lang="ts" setup>
import {defineProps, onMounted, ref, toRef, watch, withDefaults} from "vue";
import SeekNodes from "./seekNodes";

const slider_line = ref();
const seekBar = ref();
const percentage = ref();
const nodeSlider = ref();

const props = withDefaults(
    defineProps<{
        width?: string | number,
        colorTextPercent?: string,
        nodeSize?: number,
        nodeCount?: number,
        nodeColor: string,
        lineColor?: string,
        defaultPercentage?: number,
        transition?: number,
        attractionUnit?: number,
        showNodes?: boolean,
        showPercentage?: boolean
    }>(),
    {
        width: 320,
        colorTextPercent: "black",
        nodeSize: 10,
        nodeCount: 4,
        nodeColor: "gray",
        lineColor: "yellow",
        defaultPercentage: 0,
        transition: 0.2,
        attractionUnit: 5,
        showNodes: false,
        showPercentage: false
    }
);
const selected = ref<number>(props.defaultPercentage);

enum Dragger {
    Touch = "Touch",
    Click = "Click",
}

const defaultPercentage = toRef(props, "defaultPercentage");
const emits = defineEmits(["selected", "rangeFinalizer"]);

onMounted(() => {
    const cssWidth =
        typeof props.width == "string" ? props.width : props.width + "px";
    if (seekBar.value !== null) {
        seekBar.value.style.width = cssWidth;
    }

    seekBar.value.addEventListener("touchmove", (event: TouchEvent) => {
        eventLine(event, Dragger.Touch);
        window.document.body.style.userSelect = 'none'
        window.ontouchend = touchEnd;
        document.getElementsByTagName('body')[0].style.overflow = 'hidden';
    }, false)

    seekBar.value.addEventListener("mousedown", () => {
        window.document.body.style.userSelect = 'none'
        window.onmouseup = mouseUp
    })
});

watch(defaultPercentage, (newVal) => {
    let value = newVal;
    if (newVal >= 100) {
        value = 100;
    }
    if (value % 5 === 0) {
        slider_line.value.style.width = value + "%";
        selected.value = value;
    } else {
        selected.value = value + 1;
        slider_line.value.style.width = value + 1 + "%";
    }
});

function mouseupDragElement() {
    document.onmousemove = null;
}

function mouseUp() {
    mouseupDragElement()
    setTimeout(() => {
        emits('rangeFinalizer');
    }, 100)
    window.document.body.style.userSelect = 'unset'
    window.onmouseup = null
}

function touchEnd() {
    setTimeout(() => {
        emits('rangeFinalizer');
    }, 100)
    window.document.body.style.userSelect = 'unset'
    document.getElementsByTagName('body')[0].style.overflow = 'auto';
    window.ontouchend = null
}

function dragElement() {
    let client = 0;
    let percent = 0;
    const offset = seekBar.value.getBoundingClientRect();
    document.onmousemove = logKey;

    function logKey(e: MouseEvent) {
        slider_line.value.style.transition = `width ${props.transition / 2}s`;
        client = e.clientX;
        percent = Math.floor(
            ((client - offset.left - props.nodeSize / 2) / seekBar.value.clientWidth) * 100
        );
        if (percent <= 0) {
            slider_line.value.style.width = "0%";
            percentage.value.innerText = "0%";
        } else if (percent >= 100) {
            slider_line.value.style.width = 100 + "%";
            percentage.value.innerText = 100 + "%";
        } else {
            slider_line.value.style.width =
                magnetNode(percent, props.nodeCount) + "%";
            if (props.showPercentage)
                percentage.value.innerText = magnetNode(percent, props.nodeCount) + "%";
        }
        emits("selected", magnetNode(percent, props.nodeCount));
        selected.value = magnetNode(percent, props.nodeCount) ?? 0;
    }
}

function eventLine(e: MouseEvent | TouchEvent, dragger: Dragger) {
    let percent: number;
    const rect = seekBar.value.getBoundingClientRect();
    let x = 0 as number;
    if ('clientX' in e && dragger === Dragger.Click) {
        slider_line.value.style.transition = `width ${props.transition}s`;
        x = e.clientX - rect.left;
    } else if ('targetTouches' in e) {
        slider_line.value.style.transition = "none";
        let touch
        touch = e.targetTouches[0];
        if (touch !== undefined)
            x = touch.clientX - rect.left;
    } else return
    percent = Math.floor(
        ((x - props.nodeSize / 2 + 1) / seekBar.value.clientWidth) * 100
    );
    percent = magnetNode(percent, props.nodeCount) ?? 0;
    if (percent <= 0) percent = 0;
    if (percent >= 100) percent = 100;
    slider_line.value.style.width = percent + "%";
    if (props.showPercentage)
        percentage.value.innerText = percent + "%";
    selected.value = percent;
    emits("selected", magnetNode(percent, props.nodeCount));
}

function magnetNode(nodePercent: number | undefined, nodeCount: number) {
    const space = 100 / nodeCount

    for (let i = 0; i <= 100; i += space) {
        if (nodePercent !== undefined && nodePercent > i && nodePercent < i + space) {
            if (((i + i + space) / 2) <= nodePercent) {
                return i + space
            } else {
                return i
            }
        }
    }

    return nodePercent
}

</script>

<style scoped>
.seek_bar {
    margin: 0 5px 0 5px;
    -webkit-tap-highlight-color: transparent;
    user-select: none;
    display: flex;
    align-items: center;
    position: relative;
    height: 20px;
}

.line {
    margin: 10px 0;
    width: 100%;
    height: 2px;
    background-color: v-bind(nodeColor);
}

.slider_line {
    left: 0;
    display: flex;
    align-items: center;
    position: absolute;
    width: v-bind(defaultPercentage+ '%');
    height: 2px;
    background-color: v-bind(lineColor);
}

.slider_line_node {
    -webkit-user-drag: none;
    -khtml-user-drag: none;
    -moz-user-drag: none;
    -o-user-drag: none;
    -ms-user-drag: none;

    display: inline-block;
    position: absolute;
    width: v-bind(nodeSize+5 + 'px');
    height: v-bind(nodeSize+5 + 'px');
    border-radius: 150px;
    right: -10px;
    background-color: v-bind(lineColor);
    z-index: 1;
}

.percentage {
    position: absolute;
    top: -25px;
    left: 0;
    font-size: 13px;
    color: var(--gray-100);
}

body[data-theme='light'] .percentage {
    color: var(--gray-500);
}

:deep(.seekbar__subtext__dark) {
    color: var(--gray-200);
}

:deep(.seekbar__subtext__light) {
    color: var(--gray-400);
}

</style>
